/**
 * App-user auth client (magic link + JWT) — the /app/* surface only.
 * Independent of the CF Access session that gates /admin; see
 * api/src/sahab_api/auth.py::require_app_user.
 */
import { API_BASE, HttpError } from "./api";

const ACCESS_TOKEN_KEY = "sahab_access_token";
const REFRESH_TOKEN_KEY = "sahab_refresh_token";

export type AppUser = {
  id: string;
  email: string;
  role: "admin" | "employee";
  full_name: string | null;
  status: "invited" | "active" | "disabled";
  employee_id: string | null;
};

type TokenPair = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: AppUser;
};

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

function storeTokens(pair: TokenPair): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, pair.access_token);
  localStorage.setItem(REFRESH_TOKEN_KEY, pair.refresh_token);
  // Best-effort: ask the browser not to evict localStorage under storage
  // pressure (or Safari's ~7-day no-interaction cleanup for installed PWAs)
  // -- without this, the refresh token can be wiped well before its actual
  // 90-day expiry, forcing a re-login that has nothing to do with the token
  // itself being invalid. No-op where the API isn't supported.
  void navigator.storage?.persist?.();
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/* --- Own-slug cache -------------------------------------------------------
   /app is "your card", but resolving whose card it is normally costs an
   authenticated round trip, so a cold launch showed a spinner and then, if
   the session had lapsed, a login wall — even though the card itself is
   public and needs no session at all. Remembering the slug lets the icon
   paint the card immediately and defer the login prompt to the things that
   actually need it (Scan, Leads).

   The slug is public information (it's in the card's URL), so this is a
   convenience cache, not a credential. Cleared on explicit logout — someone
   signing out expects the app to forget them, and on a shared phone the next
   launch showing the previous person's card would be wrong. NOT cleared when
   a session merely expires: that isn't the user asking to be forgotten, and
   it's exactly the case where an instant card matters most. */
const MY_SLUG_KEY = "sahab_my_slug";

export function getCachedSlug(): string | null {
  return localStorage.getItem(MY_SLUG_KEY);
}

export function cacheSlug(slug: string): void {
  localStorage.setItem(MY_SLUG_KEY, slug);
}

export function clearCachedSlug(): void {
  localStorage.removeItem(MY_SLUG_KEY);
}

/** Fire-and-forget refresh of the cached slug.
 *
 * Called as the app redirects away, so it deliberately doesn't return
 * anything React waits on — it just keeps the cache honest for the *next*
 * launch if an admin ever changes the slug. Silent on failure: an expired
 * session here is normal and must not disturb a redirect that is already
 * showing the user their card. */
export function refreshCachedSlug(): void {
  if (!isLoggedIn()) return;
  void authedGetJson<{ slug: string }>("/api/me/card")
    .then((card) => {
      if (card.slug) cacheSlug(card.slug);
    })
    .catch(() => {
      // Offline, or the session lapsed — the cached slug stays as-is and the
      // user keeps seeing their card. Login is prompted by Scan/Leads.
    });
}

export function isLoggedIn(): boolean {
  return getAccessToken() !== null;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const data = (await resp.json()) as { detail?: string };
      if (data.detail) detail = data.detail;
    } catch {
      // response wasn't JSON — keep the generic HTTP status message
    }
    throw new HttpError(resp.status, detail);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

/** Always resolves — the API returns 202 regardless of whether the email
 * is provisioned, so the UI can't (and shouldn't) distinguish "unknown
 * email" from "link sent" here. */
export async function requestLoginLink(email: string): Promise<void> {
  await postJson("/api/auth/request-link", { email });
}

export async function verifyLoginToken(token: string): Promise<AppUser> {
  const pair = await postJson<TokenPair>("/api/auth/verify", { token });
  storeTokens(pair);
  return pair.user;
}

/** Redeem the 6-digit code from the sign-in email.
 *
 * This is the only sign-in path that works in an installed iOS home-screen
 * app: Mail hands the emailed link to Safari, and iOS gives the installed
 * app a storage container separate from Safari's, so a link-issued token is
 * written where the app can never read it. Typing the code keeps the whole
 * exchange inside whichever container the user is actually standing in. */
export async function verifyLoginCode(email: string, code: string): Promise<AppUser> {
  const pair = await postJson<TokenPair>("/api/auth/verify-code", { email, code });
  storeTokens(pair);
  return pair.user;
}

async function refresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  try {
    const pair = await postJson<TokenPair>("/api/auth/refresh", {
      refresh_token: refreshToken,
    });
    storeTokens(pair);
    return true;
  } catch {
    clearTokens();
    return false;
  }
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  clearTokens();
  clearCachedSlug();
  if (!refreshToken) return;
  try {
    await postJson("/api/auth/logout", { refresh_token: refreshToken });
  } catch {
    // best-effort — the local tokens are already cleared either way
  }
}

/** Fetch with the app-user bearer token, retrying once after a silent
 * refresh if the access token had expired. Throws HttpError(401) if the
 * refresh also fails (caller should send the user back to /app/login). */
export async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const doFetch = (): Promise<Response> => {
    const token = getAccessToken();
    return fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { ...init.headers, Authorization: `Bearer ${token ?? ""}` },
    });
  };

  let resp = await doFetch();
  if (resp.status === 401) {
    const refreshed = await refresh();
    if (!refreshed) {
      throw new HttpError(401, "Session expired — please sign in again.");
    }
    resp = await doFetch();
  }
  return resp;
}

export async function authedGetJson<T>(path: string): Promise<T> {
  const resp = await authedFetch(path);
  if (!resp.ok) {
    throw new HttpError(resp.status, `HTTP ${resp.status} on ${path}`);
  }
  return (await resp.json()) as T;
}

/** Fetches an authenticated image and returns a local blob: URL for it.
 * Needed because <img src> can't carry an Authorization header — the
 * card-image endpoints are proxied through the API rather than a public
 * R2 URL. Caller owns the URL and must revoke it (URL.revokeObjectURL)
 * when done, e.g. in a useEffect cleanup. */
export async function authedGetBlobUrl(path: string): Promise<string> {
  const resp = await authedFetch(path);
  if (!resp.ok) {
    throw new HttpError(resp.status, `HTTP ${resp.status} on ${path}`);
  }
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}
